import * as turf from '@turf/turf';
import type { Feature, Geometry, Polygon as GJPolygon } from 'geojson';

export function ensureClosed(ring: [number, number][]): [number, number][] {
  if (!ring?.length) return ring;
  const [fx, fy] = ring[0];
  const [lx, ly] = ring[ring.length - 1];
  return fx === lx && fy === ly ? ring : [...ring, [fx, fy]];
}
export function signedArea(ring: [number, number][]): number {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    sum += (x2 - x1) * (y2 + y1);
  }
  return -sum;
}
export function isClockwise(ring: [number, number][]): boolean {
  return signedArea(ensureClosed(ring)) < 0;
}
export function orientRing(ring: [number, number][], wantClockwise: boolean): [number, number][] {
  const r = ensureClosed(ring);
  const cw = isClockwise(r);
  return cw === wantClockwise ? r : [...r].reverse();
}
export function enforceLeftHandRings(coords: [number, number][][]): [number, number][][] {
  if (!Array.isArray(coords) || !coords.length) return coords;
  const out: [number, number][][] = [];
  out.push(orientRing(coords[0], true));
  for (let i = 1; i < coords.length; i++) out.push(orientRing(coords[i], false));
  return out;
}
export function orientedFeature(f: Feature<GJPolygon>): Feature<GJPolygon> {
  const coords = f.geometry.coordinates ?? [];
  return {
    type: 'Feature',
    properties: { ...(f.properties ?? {}) },
    geometry: { type: 'Polygon', coordinates: enforceLeftHandRings(coords as any) },
  };
}

export function normalizeForDraw(input: any): Feature<GJPolygon>[] {
  const feats: Feature[] = !input
    ? []
    : input.type === 'FeatureCollection'
      ? (input.features ?? [])
      : input.type === 'Feature'
        ? [input]
        : input.type
          ? ([{ type: 'Feature', properties: {}, geometry: input as Geometry }] as any)
          : [];

  const out: Feature<GJPolygon>[] = [];
  for (const f of feats) {
    const g = f?.geometry;
    if (!g) continue;
    if (g.type === 'Polygon') out.push(f as Feature<GJPolygon>);
    else if (g.type === 'MultiPolygon') {
      const rings = g.coordinates;
      for (let i = 0; i < rings.length; i++) {
        out.push({
          type: 'Feature',
          properties: { ...(f.properties ?? {}), _part: i },
          geometry: { type: 'Polygon', coordinates: rings[i] },
        } as any);
      }
    }
  }
  return out;
}

export type InvalidCoord = { path: string; lng: number; lat: number };

export function forEachPosition(g: any, cb: (lng: number, lat: number, path: string) => void, path = 'root') {
  if (!g) return;
  const walk = (coords: any, pth: string) => {
    if (!coords) return;
    if (typeof coords[0] === 'number') {
      cb(coords[0], coords[1], pth);
      return;
    }
    for (let i = 0; i < coords.length; i++) walk(coords[i], `${pth}/${i}`);
  };
  if (g.type === 'FeatureCollection') {
    g.features?.forEach(
      (f: any, i: number) => f?.geometry && walk(f.geometry.coordinates, `${path}.features[${i}].geometry`)
    );
  } else if (g.type === 'Feature') {
    g.geometry && walk(g.geometry.coordinates, `${path}.geometry`);
  } else if (g.type) {
    walk(g.coordinates, path);
  }
}

export function findInvalidCoord(geojson: any): InvalidCoord | null {
  let bad: InvalidCoord | null = null;
  forEachPosition(geojson, (lng, lat, path) => {
    if (bad) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      bad = { path, lng, lat };
    }
  });
  return bad;
}

export function ringLeavesLongitudeRange(ring: [number, number][]): boolean {
  return ring.some(([lng]) => !Number.isFinite(lng) || Math.abs(lng) > 180);
}

export function firstHoleOutsideRing(outerRing: [number, number][], holes: [number, number][][]): number {
  const outer = ensureClosed(outerRing);
  for (let index = 0; index < holes.length; index++) {
    if (!holeLiesWithin(ensureClosed(holes[index]), outer)) return index;
  }
  return -1;
}

function holeLiesWithin(hole: [number, number][], outer: [number, number][]): boolean {
  try {
    return turf.booleanWithin(turf.polygon([hole]), turf.polygon([outer]));
  } catch {
    return false;
  }
}

export function holeDefect(feature: Feature<GJPolygon>): boolean {
  const [outer, ...holes] = (feature.geometry?.coordinates ?? []) as [number, number][][];
  if (!outer || !holes.length) return false;
  if (![outer, ...holes].every(ringIsSimple)) return false;
  return firstHoleOutsideRing(outer, holes) >= 0 || holesOverlap(holes);
}

export function repairHoles(feature: Feature<GJPolygon>): Feature<GJPolygon>[] {
  const [outer, ...holes] = feature.geometry.coordinates as [number, number][][];
  const cut = turf.difference(turf.featureCollection([ringPolygon(outer), ...holes.map(ringPolygon)]));
  const parts = cut?.geometry.type === 'Polygon' ? [cut.geometry.coordinates] : (cut?.geometry.coordinates ?? []);
  return (parts as [number, number][][][]).map((coordinates, index) => ({
    type: 'Feature',
    ...(index === 0 && feature.id !== undefined ? { id: feature.id } : {}),
    properties: { ...(feature.properties ?? {}), shape: 'polygon', _circle: false },
    geometry: { type: 'Polygon', coordinates: enforceLeftHandRings(coordinates) },
  }));
}

function ringPolygon(ring: [number, number][]) {
  return turf.polygon([ensureClosed(ring)]);
}

function ringIsSimple(ring: [number, number][]): boolean {
  try {
    return turf.kinks(ringPolygon(ring)).features.length === 0;
  } catch {
    return false;
  }
}

function holesOverlap(holes: [number, number][][]): boolean {
  for (let i = 0; i < holes.length; i++) {
    for (let j = i + 1; j < holes.length; j++) {
      const shared = turf.intersect(turf.featureCollection([ringPolygon(holes[i]), ringPolygon(holes[j])]));
      if (shared && turf.area(shared) > 0) return true;
    }
  }
  return false;
}

export function openRing(coords: [number, number][]): [number, number][] {
  if (!coords?.length) return [];
  const a = coords[0],
    b = coords[coords.length - 1];
  return a && b && a[0] === b[0] && a[1] === b[1] ? coords.slice(0, -1) : coords;
}

export function looksLikeCircle(f: Feature<GJPolygon>, { minVertices = 16, cvTolerance = 0.02 } = {}): boolean {
  try {
    const ring = (f.geometry?.coordinates?.[0] ?? []) as [number, number][];
    const pts = openRing(ring);
    if (pts.length < minVertices) return false;

    const c = (turf.centroid(f as any) as any)?.geometry?.coordinates as [number, number];
    if (!c) return false;

    const radii = pts.map(([lng, lat]) => turf.distance([lng, lat], c, { units: 'meters' }) as number);
    const mean = radii.reduce((s, r) => s + r, 0) / radii.length;
    if (!Number.isFinite(mean) || mean <= 0) return false;
    const variance = radii.reduce((s, r) => s + (r - mean) * (r - mean), 0) / radii.length;
    const cv = Math.sqrt(Math.max(0, variance)) / mean;
    return cv <= cvTolerance;
  } catch {
    return false;
  }
}

export function isCircleFeature(f: any): boolean {
  const p = (f?.properties ?? {}) as any;
  if (p?.sc_circle === true || p?.shape === 'circle' || p?.isCircle === true || p?._shape === 'circle') return true;
  try {
    return looksLikeCircle(f as Feature<GJPolygon>);
  } catch {
    return false;
  }
}
